/**
 * V106-1-8 / V106-5-6 — client for the SOC 2 and ISO 42001 program screens.
 *
 * Every call is workspace-scoped (`/api/w/:slug/*`): the server resolves the
 * workspace and the caller from the session; no body carries a workspace id.
 *   Reads (any member): the screen aggregate, the shared next gap, entries, the
 *     Today program cards, the SoA, the certification cycle, the scope view.
 *   Writes (server-enforced per route): program entries (per kind), the SOC 2
 *     report upload (owner/admin or the SOC 2 lead), scope options (owner/admin or
 *     the lead), SoA propose / approve / withdraw, cycle milestones.
 */
import type { CollectSource, GapCounts, NextGap, ProgramEntry, ProgramEntryKind, ProgramScreen, ProgramTodayCard } from '@aigovops/core';
import { ApiError, req } from './api';

export type { CollectSource, GapCounts, NextGap, ProgramEntry, ProgramEntryKind, ProgramScreen, ProgramTodayCard };

const w = (slug: string) => `/api/w/${encodeURIComponent(slug)}`;
const prog = (slug: string, program: string) => `${w(slug)}/programs/${encodeURIComponent(program)}`;
const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

export const getProgramScreen = (slug: string, program: string) =>
  req<{ screen: ProgramScreen }>(`${prog(slug, program)}/screen`).then((r) => r.screen);

export interface NextGapView {
  program: string;
  period_id: string;
  next: NextGap | null;
  counts: GapCounts;
  collect: Record<string, CollectSource[]>;
  in_progress: Record<string, 'ticket' | 'risk_request'>;
}

export const getNextGap = (slug: string, program: string, periodId: string, skip?: string | null) =>
  req<{ next_gap: NextGapView }>(`${prog(slug, program)}/next-gap?period_id=${encodeURIComponent(periodId)}${skip ? `&skip=${encodeURIComponent(skip)}` : ''}`).then((r) => r.next_gap);

export const listProgramEntries = (slug: string, program: string, kind?: ProgramEntryKind) =>
  req<{ entries: ProgramEntry[] }>(`${prog(slug, program)}/entries${kind ? `?kind=${kind}` : ''}`).then((r) => r.entries);

export const recordProgramEntry = (slug: string, program: string, body: { kind: ProgramEntryKind; subject_key: string | null; data?: Record<string, unknown> }) =>
  req<{ entry: ProgramEntry; ended: string | null }>(`${prog(slug, program)}/entries`, post(body)).then((r) => r.entry);

export const endProgramEntry = (slug: string, program: string, entryId: string, reason?: string) =>
  req<{ entry: ProgramEntry }>(`${prog(slug, program)}/entries/${encodeURIComponent(entryId)}/end`, post(reason ? { reason } : {})).then((r) => r.entry);

async function sendMultipart<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, init); // raw-fetch-ok: FormData multipart upload
  if (!res.ok) {
    let message = res.statusText;
    let parsed: unknown;
    try { parsed = await res.json(); const b = parsed as { error?: string; detail?: string }; if (b?.error) message = b.detail ? `${b.error} — ${b.detail}` : b.error; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, parsed);
  }
  return (await res.json()) as T;
}

/** POST a FormData body (one line so the SPA-route gate reads its method). */
const multipart = <T,>(path: string, form: FormData) => sendMultipart<T>(path, { method: 'POST', body: form });

/** A file entry (certification-body results / decision / concern answer): the file is stored with its SHA-256. */
export function uploadProgramEntry(slug: string, program: string, input: { kind: ProgramEntryKind; subject_key: string; data: Record<string, unknown>; file: File }) {
  const form = new FormData();
  form.set('kind', input.kind);
  form.set('subject_key', input.subject_key);
  form.set('data', JSON.stringify(input.data));
  form.set('file', input.file);
  return multipart<{ entry: ProgramEntry }>(`${prog(slug, program)}/entries/upload`, form).then((r) => r.entry);
}

export const getTodayPrograms = (slug: string) =>
  req<{ cards: ProgramTodayCard[] }>(`${w(slug)}/today/programs`).then((r) => r.cards);

/** Attach the issued SOC 2 report PDF (owner/admin or the SOC 2 lead). Off-platform: opinion + date too. */
export function uploadSoc2Report(slug: string, periodId: string, input: { file: File; opinion_type?: string; report_date?: string }) {
  const form = new FormData();
  form.set('file', input.file);
  if (input.opinion_type) form.set('opinion_type', input.opinion_type);
  if (input.report_date) form.set('report_date', input.report_date);
  return multipart<{ report: { report_id: string; opinion_type: string; report_date: string }; linked_materials: Array<{ material_id: string; profile_id: string; expires_at: string }> }>(
    `${w(slug)}/audit-periods/${encodeURIComponent(periodId)}/soc2-reports/upload`, form);
}

/** SOC 2 categories in scope (owner/admin or the profile's lead). */
export const setScopeOptions = (slug: string, profileId: string, body: { categories: string[]; privacy_role?: string | null }, appliesTo: 'current' | 'next' = 'current') =>
  req<{ profile: unknown }>(`${w(slug)}/certification-profiles/${encodeURIComponent(profileId)}/scope-options?applies_to=${appliesTo}`, { method: 'PUT', body: JSON.stringify(body) });

// ---- ISO 42001 ---------------------------------------------------------------------------

export interface SoaEntryView { requirement_id: string; ref: string; title: string; applicability: 'applicable' | 'excluded'; justification: string; implementation_note: string | null }
export interface SoaVersionView {
  soa_version_id: string; version: number; status: 'proposed' | 'approved' | 'superseded' | 'withdrawn'; note: string | null;
  entry_count: number; excluded_count: number; authored_by: string; authored_at: string; approved_by: string | null; approved_at: string | null;
  withdrawn_by: string | null; withdrawn_at: string | null; superseded_at: string | null; package_id: string | null; entries?: SoaEntryView[];
}
export interface SoaOverviewView {
  program: string; profile_id: string;
  governed: Array<{ requirement_id: string; ref: string; title: string; group_ref: string | null }>;
  current: (SoaVersionView & { entries: SoaEntryView[] }) | null;
  proposed: SoaVersionView | null;
  counts: { governed: number; applicable: number | null; excluded: number | null };
}

export const getSoa = (slug: string, program: string) => req<{ soa: SoaOverviewView }>(`${prog(slug, program)}/soa`).then((r) => r.soa);
export const listSoaVersions = (slug: string, program: string) => req<{ versions: SoaVersionView[] }>(`${prog(slug, program)}/soa/versions`).then((r) => r.versions);
export const getSoaVersion = (slug: string, program: string, versionId: string) =>
  req<{ version: SoaVersionView & { entries: SoaEntryView[] } }>(`${prog(slug, program)}/soa/versions/${encodeURIComponent(versionId)}`).then((r) => r.version);
export const proposeSoa = (slug: string, program: string, body: { entries: Array<{ requirement_id: string; applicability: string; justification: string; implementation_note?: string | null }>; note?: string | null }) =>
  req<{ version: SoaVersionView }>(`${prog(slug, program)}/soa/versions`, post(body)).then((r) => r.version);
export const approveSoa = (slug: string, program: string, versionId: string) =>
  req<{ version: SoaVersionView; package_id: string }>(`${prog(slug, program)}/soa/versions/${encodeURIComponent(versionId)}/approve`, post({}));
export const withdrawSoa = (slug: string, program: string, versionId: string, reason?: string | null) =>
  req<{ version: SoaVersionView }>(`${prog(slug, program)}/soa/versions/${encodeURIComponent(versionId)}/withdraw`, post(reason ? { reason } : {})).then((r) => r.version);

export interface CycleEventView {
  key: string; label: string; kind: string; anchor: string; anchor_on: string | null; anchor_estimated: boolean;
  due_on: string | null; deadline_on: string | null; done_on: string | null; status: 'done' | 'overdue' | 'due' | 'planned'; basis: string; source: string;
}
export interface CycleView {
  program: string; profile_id: string;
  rule_set: { rule_set_id: string; version: number; effective_from: string } | null;
  schedule: { cycle_no: number; cycle_start_on: string | null; certification_decision_on: string | null; certification_decision_estimated: boolean; expires_on: string | null; events: CycleEventView[] } | null;
  milestones: Array<{ milestone_id: string; cycle_no: number; milestone_key: string; occurred_on: string; note: string | null; recorded_by: string; recorded_at: string }>;
  milestone_keys: string[];
}
export const getCertificationCycle = (slug: string, program: string) => req<{ cycle: CycleView }>(`${prog(slug, program)}/certification-cycle`).then((r) => r.cycle);
export const recordCycleMilestone = (slug: string, program: string, body: { milestone_key: string; occurred_on: string; note?: string | null }) =>
  req<{ milestone: CycleView['milestones'][number] }>(`${prog(slug, program)}/certification-cycle/milestones`, post(body)).then((r) => r.milestone);

export interface ScopeViewView {
  program: string; profile_id: string; period_id: string | null;
  systems: { source: string; items: Array<{ system_id: string; name: string; handles_federal_data: boolean | null; internet_reachable: boolean | null; providers: string[] }> };
  components: { source: string; items: Array<{ component_id: string; component_key: string; function: string; kind: string }> };
  interconnections: Array<{ interconnection_id: string; component_id: string; remote_name: string | null; external_service_id: string | null; direction: string }>;
  counts: { systems: number; components: number; interconnections: number };
}
export const getScopeView = (slug: string, program: string) => req<{ scope_view: ScopeViewView }>(`${prog(slug, program)}/scope-view`).then((r) => r.scope_view);

/** Undo an evidence upload: delete the artifact (owner/admin/editor; refused while it is bound elsewhere). */
export const deleteEvidenceArtifact = (slug: string, artifactId: string) =>
  req<unknown>(`${w(slug)}/evidence/${encodeURIComponent(artifactId)}`, { method: 'DELETE' });

/** Record an interview for objectives of a period (owner/admin or the program lead; the caller is the interviewer). */
export const recordInterview = (slug: string, program: string, periodId: string, body: { interviewee_user_id?: string | null; interviewee_name?: string | null; conducted_at: string; topic: string; objective_ids: string[]; notes?: string | null }) =>
  req<{ interview: { interview_id: string } }>(`${prog(slug, program)}/periods/${encodeURIComponent(periodId)}/interviews`, post(body)).then((r) => r.interview);

// ---- D-106-18 / D-106-19: what each point is missing, and results waiting on review ------------

export type { ObjectiveInputs, ObjectiveInput, MissingReason } from '@aigovops/core';

/** Any member: per objective what it lacks + the hero summary; `can_review` = owner/admin or the lead. */
export const getObjectiveInputs = (slug: string, program: string, periodId: string) =>
  req<{ inputs: import('@aigovops/core').ObjectiveInputs; can_review: boolean }>(`${prog(slug, program)}/periods/${encodeURIComponent(periodId)}/objective-inputs`);
